
import { useParams, useNavigate } from 'react-router-dom';
import { useQC } from './hooks/useQC';
import { useState, useCallback, useEffect } from 'react';
import type { QCChecklistItem } from './types';

export default function QCInspectionDetail() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { getOrder, getInspection, updateInspection, completeInspection } = useQC();
  
  const orderQuery = getOrder(id);
  const inspectionQuery = getInspection(id);
  
  const [notes, setNotes] = useState('');
  const [photos, setPhotos] = useState<string[]>([]);
  const [checklistItems, setChecklistItems] = useState<QCChecklistItem[]>([]);
  
  const order = orderQuery.data;
  const inspection = inspectionQuery.data;
  
  // Initialize checklist items when inspection data loads
  useEffect(() => {
    if (inspection?.checklistItems) {
      setChecklistItems(inspection.checklistItems);
      setNotes(inspection.notes || '');
      setPhotos(inspection.photos || []);
    }
  }, [inspection]);
  
  const updateChecklistItem = useCallback((itemId: string, updates: Partial<QCChecklistItem>) => {
    setChecklistItems(prev => 
      prev.map(item => 
        item.id === itemId ? { ...item, ...updates } : item
      )
    );
  }, []);
  
  const handlePhotoUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      // In a real app, you would upload to a file storage service
      const newPhotos = Array.from(files).map(file => URL.createObjectURL(file));
      setPhotos(prev => [...prev, ...newPhotos]);
    }
  };
  
  const validateSubmission = (decision: 'approved' | 'rejected') => {
    const requiredItems = checklistItems.filter(item => item.isRequired);
    const incompleteRequired = requiredItems.filter(item => item.status === 'pending');
    
    if (incompleteRequired.length > 0) {
      alert('Please complete all required checklist items before submission.');
      return false;
    }
    
    if (decision === 'rejected' && !notes.trim()) {
      alert('Please provide notes explaining the rejection reason.');
      return false;
    }
    
    return true;
  };
  
  const handleComplete = async (decision: 'approved' | 'rejected') => {
    if (!validateSubmission(decision)) return;
    
    try {
      await completeInspection.mutateAsync({
        id: inspection?.id || '',
        decision,
        notes,
        checklistItems
      });
      navigate('/qc/orders');
    } catch (error) {
      console.error('Failed to complete inspection:', error);
    }
  };
  
  const getItemStatusColor = (status: string) => {
    switch (status) {
      case 'passed': return 'text-green-600 bg-green-100';
      case 'failed': return 'text-red-600 bg-red-100';
      case 'na': return 'text-gray-600 bg-gray-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };
  
  if (orderQuery.isLoading || inspectionQuery.isLoading) {
    return <div className="p-6">Loading...</div>;
  }
  
  if (!order || !inspection) {
    return <div className="p-6">Order or inspection not found</div>;
  }
  
  const completedItems = checklistItems.filter(item => item.status !== 'pending').length;
  const totalItems = checklistItems.length;
  const progressPercentage = totalItems > 0 ? (completedItems / totalItems) * 100 : 0;
  
  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">QC Inspection</h1>
          <p className="text-gray-600">Order: {order.orderNo}</p>
        </div>
        <button
          onClick={() => navigate('/qc/orders')}
          className="btn-secondary flex items-center"
        >
          <i className="ri-arrow-left-line mr-2"></i>
          Back to Orders
        </button>
      </div>
      
      {/* Order Information */}
      <div className="grid md:grid-cols-2 gap-6">
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <i className="ri-information-line text-[var(--color-primary)] mr-2"></i>
            Order Details
          </h3>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-gray-600">Product:</span>
              <span className="font-medium">{order.product}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Customer:</span>
              <span className="font-medium">{order.customer}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Vendor:</span>
              <span className="font-medium">{order.vendor}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Priority:</span>
              <span className={`badge ${order.priority === 'high' ? 'text-red-600 bg-red-100' : order.priority === 'medium' ? 'text-yellow-600 bg-yellow-100' : 'text-green-600 bg-green-100'}`}>
                {order.priority}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Due Date:</span>
              <span className="font-medium">{new Date(order.dueDate).toLocaleDateString()}</span>
            </div>
          </div>
        </div>
        
        <div className="card">
          <h3 className="text-lg font-semibold mb-4 flex items-center">
            <i className="ri-progress-line text-[var(--color-primary)] mr-2"></i>
            Inspection Progress
          </h3>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-2">
                <span>Completed Items</span>
                <span>{completedItems}/{totalItems}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className="bg-[var(--color-primary)] h-2 rounded-full transition-all duration-300"
                  style={{ width: `${progressPercentage}%` }}
                ></div>
              </div>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Status:</span>
              <span className={`badge ${inspection.status === 'completed' ? 'approved' : 'pending'}`}>
                {inspection.status}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Started:</span>
              <span>{inspection.startedAt ? new Date(inspection.startedAt).toLocaleString() : 'Not started'}</span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Quality Checklist */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-checkbox-line text-[var(--color-primary)] mr-2"></i>
          Quality Checklist
        </h3>
        <div className="space-y-4">
          {checklistItems.map((item) => (
            <div key={item.id} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{item.description}</h4>
                  <p className="text-sm text-gray-600">{item.category}</p>
                  {item.isRequired && (
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 mt-1">
                      Required
                    </span>
                  )}
                </div>
                <span className={`badge ${getItemStatusColor(item.status)}`}>
                  {item.status}
                </span>
              </div>
              
              <div className="grid md:grid-cols-4 gap-2 mb-3">
                <button
                  onClick={() => updateChecklistItem(item.id, { status: 'passed' })}
                  className={`btn-sm ${item.status === 'passed' ? 'bg-green-600 text-white' : 'bg-gray-100 text-gray-700'} hover:bg-green-600 hover:text-white transition-colors`}
                >
                  <i className="ri-check-line mr-1"></i>
                  Pass
                </button>
                <button
                  onClick={() => updateChecklistItem(item.id, { status: 'failed' })}
                  className={`btn-sm ${item.status === 'failed' ? 'bg-red-600 text-white' : 'bg-gray-100 text-gray-700'} hover:bg-red-600 hover:text-white transition-colors`}
                >
                  <i className="ri-close-line mr-1"></i>
                  Fail
                </button>
                <button
                  onClick={() => updateChecklistItem(item.id, { status: 'na' })}
                  className={`btn-sm ${item.status === 'na' ? 'bg-gray-600 text-white' : 'bg-gray-100 text-gray-700'} hover:bg-gray-600 hover:text-white transition-colors`}
                >
                  <i className="ri-subtract-line mr-1"></i>
                  N/A
                </button>
                <button
                  onClick={() => updateChecklistItem(item.id, { status: 'pending' })}
                  className={`btn-sm ${item.status === 'pending' ? 'bg-yellow-600 text-white' : 'bg-gray-100 text-gray-700'} hover:bg-yellow-600 hover:text-white transition-colors`}
                >
                  <i className="ri-time-line mr-1"></i>
                  Reset
                </button>
              </div>
              
              <textarea
                placeholder="Add notes for this item..."
                className="input text-sm"
                rows={2}
                value={item.notes || ''}
                onChange={(e) => updateChecklistItem(item.id, { notes: e.target.value })}
              />
            </div>
          ))}
        </div>
      </div>
      
      {/* Photos Section */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-camera-line text-[var(--color-primary)] mr-2"></i>
          Inspection Photos
        </h3>
        <div className="space-y-4">
          <div>
            <label className="btn-secondary cursor-pointer inline-flex items-center">
              <i className="ri-upload-line mr-2"></i>
              Upload Photos
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
            </label>
          </div>
          
          {photos.length > 0 && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {photos.map((photo, index) => (
                <div key={index} className="relative">
                  <img
                    src={photo}
                    alt={`Inspection photo ${index + 1}`}
                    className="w-full h-32 object-cover rounded-lg border border-gray-200"
                  />
                  <button
                    onClick={() => setPhotos(prev => prev.filter((_, i) => i !== index))}
                    className="absolute top-2 right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center text-xs hover:bg-red-600"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      
      {/* General Notes */}
      <div className="card">
        <h3 className="text-lg font-semibold mb-4 flex items-center">
          <i className="ri-file-text-line text-[var(--color-primary)] mr-2"></i>
          General Notes
        </h3>
        <textarea
          placeholder="Add general inspection notes..."
          className="input"
          rows={4}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
        />
      </div>
      
      {/* Action Buttons */}
      <div className="flex items-center justify-end space-x-4 pt-6 border-t border-gray-200">
        <button
          onClick={() => handleComplete('rejected')}
          className="btn-danger flex items-center"
          disabled={completeInspection.isPending}
        >
          <i className="ri-close-line mr-2"></i>
          Reject Order
        </button>
        <button
          onClick={() => handleComplete('approved')}
          className="btn-primary flex items-center"
          disabled={completeInspection.isPending}
        >
          <i className="ri-check-line mr-2"></i>
          Approve Order
        </button>
      </div>
    </div>
  );
}
